/**
 * pricingModel - the pricing vocabulary, for ANY entity that is priced.
 *
 * This was `Matters/economics/economicsConstants`, and it was matter-only for
 * no reason other than matters getting there first: a pricing model, a
 * currency, a budget, a fixed fee and a contingency describe a project exactly
 * as well as they describe a matter. It moved here whole, and
 * `economicsConstants` re-exports every name it used to own, so nothing that
 * imports the matter spelling had to change.
 *
 * Label strings are i18n KEYS (passed through `t()` at render time), not
 * user-facing text - see the locale files.
 */

import type { PricingModel } from '../../../types/economics';

/** Ordered list of pricing models for selectors (US #2235). */
export const PRICING_MODELS: PricingModel[] = [
  'hourly',
  'fixed_fee',
  'contingency',
  'hybrid',
];

export const DEFAULT_PRICING_MODEL: PricingModel = 'hourly';

/** i18n keys for each pricing model's display label. */
export const PRICING_MODEL_LABEL_KEYS: Record<PricingModel, string> = {
  hourly: 'Hourly',
  fixed_fee: 'Fixed fee',
  contingency: 'Contingency',
  hybrid: 'Hybrid',
};

/** Whether a pricing model requires a fixed-fee amount. */
export const requiresFixedFee = (model?: string | null): boolean =>
  model === 'fixed_fee' || model === 'hybrid';

/** Whether a pricing model requires a contingency percentage. */
export const requiresContingency = (model?: string | null): boolean =>
  model === 'contingency' || model === 'hybrid';

/**
 * Editable shape of the pricing fields. Amounts are kept as strings because
 * they are bound to text inputs; they are parsed/validated on submit.
 */
export interface PricingValue {
  pricing_model: PricingModel;
  currency: string;
  fixed_fee_amount: string;
  /**
   * Budget / fee cap. Optional on every model - an empty string means "no
   * budget", which is a legitimate answer and not a validation failure.
   */
  budget_amount: string;
  contingency_pct: string;
}

export const emptyPricing = (currency: string): PricingValue => ({
  pricing_model: DEFAULT_PRICING_MODEL,
  currency,
  fixed_fee_amount: '',
  budget_amount: '',
  contingency_pct: '',
});

/**
 * Validate a required numeric field given i18n error keys. Returns an error key
 * or undefined. `min`/`max` are inclusive; pass undefined to skip a bound.
 */
const validateNumber = (
  raw: string,
  keys: { required: string; invalid: string; range: string },
  min?: number,
  max?: number,
): string | undefined => {
  const trimmed = raw.trim();
  if (trimmed === '') return keys.required;
  const n = Number(trimmed);
  if (Number.isNaN(n)) return keys.invalid;
  if ((min !== undefined && n < min) || (max !== undefined && n > max)) return keys.range;
  return undefined;
};

/**
 * Validate the pricing fields for the currently-selected model.
 * Returns a map of field name -> i18n error KEY. Empty map means valid.
 *
 * Edge cases (intentionally allowed): fixed fee of exactly 0, contingency of
 * exactly 100%.
 */
export const getPricingErrors = (
  value: PricingValue,
): Partial<Record<'fixed_fee_amount' | 'budget_amount' | 'contingency_pct', string>> => {
  const errors: Partial<Record<'fixed_fee_amount' | 'budget_amount' | 'contingency_pct', string>> = {};

  /*
    Budget is OPTIONAL on every model, so an empty field is valid and only a
    filled one is checked. `validateNumber` treats empty as missing, which is
    right for a required field and wrong here.
  */
  const budget = value.budget_amount.trim();
  if (budget !== '') {
    const n = Number(budget);
    if (Number.isNaN(n)) errors.budget_amount = 'Enter a valid amount';
    else if (n < 0) errors.budget_amount = 'Amount must be 0 or greater';
  }

  if (requiresFixedFee(value.pricing_model)) {
    errors.fixed_fee_amount = validateNumber(
      value.fixed_fee_amount,
      { required: 'Fixed fee amount is required', invalid: 'Enter a valid amount', range: 'Amount must be 0 or greater' },
      0,
    );
  }

  if (requiresContingency(value.pricing_model)) {
    errors.contingency_pct = validateNumber(
      value.contingency_pct,
      { required: 'Contingency percentage is required', invalid: 'Enter a valid percentage', range: 'Percentage must be between 0 and 100' },
      0,
      100,
    );
  }

  // Drop keys that came back undefined.
  if (!errors.fixed_fee_amount) delete errors.fixed_fee_amount;
  if (!errors.budget_amount) delete errors.budget_amount;
  if (!errors.contingency_pct) delete errors.contingency_pct;
  return errors;
};
